import { Box, Switch, Typography } from "@mui/material"
import MuiAlert from "@mui/material/Alert"
import Snackbar from "@mui/material/Snackbar"
import React, { useState } from "react"
import { useDispatch } from "react-redux"

import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data"
import {
	resetCreateShiftPurpose,
	resetDeleteShiftPurpose,
	resetEditShiftPurpose,
	resetUpdateShiftPurposeStatus,
} from "../../../../store/gat-admin-slice"
import ShiftPurposeForm, {
	ShiftPurposeFormValues,
} from "./ui/dialog/shift-purpose-form"
import ShiftPurposeTable from "./ui/table"

interface IShiftPurposeProps {
	newShiftSettings: any
	setNewShiftSettings: any
}

function ShiftPurpose({
	newShiftSettings,
	setNewShiftSettings,
}: IShiftPurposeProps) {
	const [open, setOpen] = useState(false)
	const [mode, setMode] = useState<"add" | "edit">("add")
	const [initialData, setInitialData] = useState<ShiftPurposeFormValues | null>(null)

	const dispatch = useDispatch()

	const {
		createShiftPurposeStatus,
		createShiftPurposeErrorMessage,
		editShiftPurposeStatus,
		editShiftPurposeErrorMessage,
		deleteShiftPurposeStatus,
		deleteShiftPurposeErrorMessage,
		updateShiftPurposeStatusStatus,
		updateShiftPurposeStatusErrorMessage,
	} = useGatAdminConfigData()

	const handleOpenAdd = () => {
		setMode("add")
		setInitialData(null)
		setOpen(true)
	}

	const handleOpenEdit = (row: any) => {
		setMode("edit")
		setInitialData({
			id: row.id,
			name: row.name,
			isDetailFieldEnabled: !!row.detailField,
			detailFieldLabel: row.detailField?.label ?? "",
			detailFieldMaxLength: row.detailField?.maxLength,
		})
		setOpen(true)
	}

	const handleClose = () => setOpen(false)

	return (
		<Box
			sx={{
				backgroundColor: "#F5F5F5",
				borderRadius: "8px",
				padding: "16px",
				marginTop: "32px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Allow to select Shift Purpose while posting Shifts
					</Typography>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						Supervisors can select the Purpose of the Shift.
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Switch
						checked={newShiftSettings.shiftPurpose ?? false}
						onChange={(e) =>
							setNewShiftSettings((prevSettings: any) => ({
								...prevSettings,
								shiftPurpose: e.target.checked,
							}))
						}
					/>
				</Box>
			</Box>

			{newShiftSettings.shiftPurpose ? (
				<ShiftPurposeTable
					handleOpenEdit={handleOpenEdit}
					handleOpenAdd={handleOpenAdd}
				/>
			) : null}

			<ShiftPurposeForm
				open={open}
				onClose={handleClose}
				mode={mode}
				initialData={initialData}
			/>

			<Snackbar
				open={createShiftPurposeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetCreateShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Shift Purpose added successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={createShiftPurposeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetCreateShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{createShiftPurposeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={editShiftPurposeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Shift Purpose updated successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={editShiftPurposeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{editShiftPurposeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={deleteShiftPurposeStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Shift Purpose deleted successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={deleteShiftPurposeStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteShiftPurpose())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{deleteShiftPurposeErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={updateShiftPurposeStatusStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetUpdateShiftPurposeStatus())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Shift Purpose status updated successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={updateShiftPurposeStatusStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetUpdateShiftPurposeStatus())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{updateShiftPurposeStatusErrorMessage ?? "An error occurred"}
				</MuiAlert>
			</Snackbar>
		</Box>
	)
}

export default ShiftPurpose
